import {
  Button,
  Chip,
  Group,
  Stack,
  Switch,
  Textarea,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import * as df from "date-fns";
import { client } from "@/api-client";
import { Header } from "@/components/Header";
import { YearMonthSelect } from "@/components/YearMonthSelect";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { useUser } from "@/UserContext";

// Types are now imported directly from @/api-types

type DogFormValues = {
  name: string;
  breed: string;
  birthDate: Date | null;
  energyLevel: string;
  temperament: string;
  trainingNeeds: string;
  healthConcerns: string;
  sterilised: boolean | undefined;
  medication: string;
};

const DogProfileForm = ({
  initialValues,
}: {
  initialValues: DogFormValues;
}) => {
  const { navigate } = useNavigator();

  const form = useForm({
    mode: "uncontrolled",
    initialValues,

    validate: {
      name: isNotEmpty("Dog name is required"),
      breed: isNotEmpty("Dog breed is required"),
      birthDate: isNotEmpty("Dog birth date is required"),
      energyLevel: isNotEmpty("Energy level is required"),
      temperament: isNotEmpty("Temperament is required"),
    },
  });

  const updateDogsProfile = client.useMutation("post", "/owner/profile/dogs");

  const handleSubmit = async (values: typeof form.values) => {
    const dog = values;
    // biome-ignore lint/style/noNonNullAssertion: there is a validation
    const date = df.format(dog.birthDate!, "yyyy-MM-dd");
    await updateDogsProfile.mutateAsync({
      body: {
        dogs: [
          {
            name: dog.name,
            breed: dog.breed,
            birthDate: date,
            energyLevel: dog.energyLevel,
            temperament: dog.temperament,
            trainingNeeds: dog.trainingNeeds,
            healthConcerns: dog.healthConcerns,
            sterilised: dog.sterilised,
            medication: dog.medication,
          },
        ],
      },
    });
    navigate({ path: UniversalRoutes.PROFILE.HOME });
  };

  return (
    <>
      <Header title="Dog Information" />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            withAsterisk
            label="Dog's Name"
            key={form.key("name")}
            {...form.getInputProps("name")}
          />
          <TextInput
            withAsterisk
            label="Dog's Breed"
            key={form.key("breed")}
            {...form.getInputProps("breed")}
          />
          <YearMonthSelect
            label="Dog's birth year and month"
            key={form.key("birthDate")}
            value={form.getValues().birthDate}
            {...form.getInputProps("birthDate")}
          />
          <Stack gap="xs">
            <label htmlFor="energyLevel">Energy Level</label>
            <Chip.Group
              multiple={false}
              key={form.key("energyLevel")}
              {...form.getInputProps("energyLevel")}
            >
              <Group>
                <Chip value="low">Low</Chip>
                <Chip value="medium">Medium</Chip>
                <Chip value="high">High</Chip>
              </Group>
            </Chip.Group>
          </Stack>
          <Stack gap="xs">
            <label htmlFor="temperament">Temperament</label>
            <Chip.Group
              multiple={false}
              key={form.key("temperament")}
              {...form.getInputProps("temperament")}
            >
              <Group>
                <Chip value="shy">Shy</Chip>
                <Chip value="neutral">Neutral</Chip>
                <Chip value="forward">Forward</Chip>
              </Group>
            </Chip.Group>
          </Stack>
          <Textarea
            label="Training Needs"
            placeholder="Describe any training needs or goals"
            key={form.key("trainingNeeds")}
            {...form.getInputProps("trainingNeeds")}
          />
          <Textarea
            label="Health Concerns"
            placeholder="Describe any health concerns or conditions"
            key={form.key("healthConcerns")}
            {...form.getInputProps("healthConcerns")}
          />

          <Switch
            label="Sterilised / Neutered"
            description="Is your dog sterilised or neutered?"
            key={form.key("sterilised")}
            {...form.getInputProps("sterilised", { type: "checkbox" })}
          />

          <Textarea
            label="Medication"
            placeholder="List any medications your dog is currently taking (leave empty if none)"
            key={form.key("medication")}
            {...form.getInputProps("medication")}
          />

          <Group justify="flex-end" mt="md">
            <Button
              type="submit"
              fullWidth
              loading={updateDogsProfile.isPending}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  );
};

const DogProfileDataLoader = () => {
  const user = useUser();
  const profile = client.useQuery(
    "get",
    "/owner/profile",
    {
      enabled: user.isProfileCreated,
    },
    {
      gcTime: 0,
      staleTime: 0,
    },
  );

  if (!profile.data) {
    return <div>Loading...</div>;
  }

  const dog = profile.data.dogs[0];
  const initialValues: DogFormValues = {
    name: dog.name,
    breed: dog.breed,
    birthDate: new Date(dog.birthDate),
    energyLevel: dog.energyLevel,
    temperament: dog.temperament,
    trainingNeeds: dog.trainingNeeds || "",
    healthConcerns: dog.healthConcerns || "",
    sterilised: dog.sterilised ?? undefined,
    medication: dog.medication || "",
  };

  return <DogProfileForm initialValues={initialValues} />;
};

export function DogsProfile() {
  const user = useUser();

  if (!user.isProfileCreated) {
    // New profile - use default values
    const defaultValues: DogFormValues = {
      name: "",
      breed: "",
      birthDate: null,
      energyLevel: "medium",
      temperament: "neutral",
      trainingNeeds: "",
      healthConcerns: "",
      sterilised: undefined,
      medication: "",
    };
    return <DogProfileForm initialValues={defaultValues} />;
  }

  return <DogProfileDataLoader />;
}
